import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Info } from '@/components/icons/barrel';
import { CapitalStatus } from '@/types';
import { useFetchAccount } from '../api/get-account';
import { useCapitalPlans } from '@/features/vest-capital/api/get-capital-plans';
import { useMemo } from 'react';
import {
  bigIntToDecimalStr,
  formatBigInt,
  formatNumber,
  parseDecimalToBigInt,
} from '@/utils/value-format';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { useCurrentAccount } from '@/store/use-account-store';

interface CapitalIndicatorsProps {
  capitalStatus?: CapitalStatus;
  hideGoal?: boolean;
}

export default function CapitalIndicators({
  capitalStatus,
  hideGoal,
}: CapitalIndicatorsProps) {
  const accountValue = useCurrentAccount((state) => state.value.bigint);

  const { data: account } = useFetchAccount();
  const { data: capitalPlans } = useCapitalPlans();

  const { t } = useTranslation();

  const { startingBalance, maxDailyLossPct } = useMemo(() => {
    if (!capitalPlans || !account?.capital)
      return { startingBalance: 0n, maxDailyLossPct: 0 };
    const activeCapitalPlan = account.capital.activeCapitalPlan;
    const activePlanMetadata = capitalPlans?.find(
      (plan) => plan.name === activeCapitalPlan,
    );
    if (!activePlanMetadata) return { startingBalance: 0n, maxDailyLossPct: 0 };
    return {
      maxDailyLossPct: Number(activePlanMetadata.metadata.max_daily_loss),
      startingBalance: parseDecimalToBigInt(
        (Number(activePlanMetadata.metadata.account_size) / 100).toString(),
        COLLATERAL_DECIMALS,
      ),
    };
  }, [capitalPlans, account?.capital?.activeCapitalPlan]);

  if (!account?.capital) return;

  const targetEquity = parseDecimalToBigInt(
    account.capital.targetEquity,
    COLLATERAL_DECIMALS,
  );
  const maxDrawdownLimit = parseDecimalToBigInt(
    account.capital.maxDrawdownLimit,
    COLLATERAL_DECIMALS,
  );
  const maxDailyLossLimit = parseDecimalToBigInt(
    account.capital.maxDailyLossLimit,
    COLLATERAL_DECIMALS,
  );
  const priorDayAccountValue = parseDecimalToBigInt(
    account.capital.priorDayAccountValue ?? account.capital.priorDayBalance,
    COLLATERAL_DECIMALS,
  );

  const priorDayMaxDailyLossLimit =
    (priorDayAccountValue * (100n - BigInt(maxDailyLossPct))) / 100n;
  const withdrawnAmount = priorDayMaxDailyLossLimit - maxDailyLossLimit;

  const maxDrawdown = startingBalance - maxDrawdownLimit;
  const maxDailyLoss =
    priorDayAccountValue - maxDailyLossLimit - withdrawnAmount;

  const dailyLoss =
    capitalStatus === CapitalStatus.GOAL_FAILED_MAX_DAILY_LOSS ||
    capitalStatus === CapitalStatus.FUNDED_FAILED_MAX_DAILY_LOSS
      ? maxDailyLoss
      : priorDayAccountValue - accountValue - withdrawnAmount;
  const drawdown =
    capitalStatus === CapitalStatus.GOAL_FAILED_MAX_DRAWDOWN ||
    capitalStatus === CapitalStatus.FUNDED_FAILED_MAX_DRAWDOWN
      ? maxDrawdown
      : accountValue > startingBalance
        ? 0n
        : startingBalance - accountValue;

  return (
    <div className="flex w-full flex-col gap-2 px-4 py-0">
      <div className={cn('flex w-full flex-col gap-0')}>
        <div className="space-y-0">
          {!hideGoal && (
            <div className="flex h-10 w-full items-center justify-between text-vestgrey-100">
              <div className="flex items-center gap-2">
                <span className="text-vestgrey-100">
                  {t('capitalIndicators.goalShort')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    accountValue >= startingBalance
                      ? 'text-green'
                      : 'text-vestgrey-100',
                  )}
                >
                  {formatNumber(
                    bigIntToDecimalStr(
                      capitalStatus === CapitalStatus.FUNDED
                        ? targetEquity - startingBalance
                        : accountValue >= startingBalance
                          ? accountValue - startingBalance
                          : 0n,
                      COLLATERAL_DECIMALS,
                    ),
                    { digits: 2, style: 'currency', showChange: true },
                  )}
                </span>
                /
                <span className="text-vestgrey-100">
                  {formatNumber(
                    bigIntToDecimalStr(
                      targetEquity - startingBalance,
                      COLLATERAL_DECIMALS,
                    ),
                    { digits: 2, style: 'currency' },
                  )}
                </span>
              </div>
            </div>
          )}

          {startingBalance > 0n && (
            <div className="flex h-10 w-full items-center justify-between text-vestgrey-100">
              <div className="flex items-center gap-2">
                <span className="text-vestgrey-100">
                  {t('capitalIndicators.dailyLossShort')}
                </span>
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger>
                      <Info size={12} className="text-vestgrey-400" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <div>{t('capitalIndicators.dailyLossTooltip')}</div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    dailyLoss > 0n ? 'text-red' : 'text-vestgrey-100',
                  )}
                >
                  {formatNumber(
                    dailyLoss > 0n
                      ? bigIntToDecimalStr(dailyLoss, COLLATERAL_DECIMALS)
                      : 0,
                    { digits: 2, style: 'currency' },
                  )}
                </span>
                /
                <span className="text-vestgrey-100">
                  {formatNumber(
                    bigIntToDecimalStr(maxDailyLoss, COLLATERAL_DECIMALS),
                    { digits: 2, style: 'currency' },
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Drawdown Progress */}
          <div className="flex h-10 w-full items-center justify-between text-vestgrey-100">
            <div className="flex items-center gap-2">
              <span className="text-vestgrey-100">
                {t('capitalIndicators.drawdownShort')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={cn(drawdown > 0n ? 'text-red' : 'text-vestgrey-100')}
              >
                {formatNumber(
                  formatBigInt(drawdown, COLLATERAL_DECIMALS, {
                    abs: true,
                    commas: false,
                  }),
                  { digits: 2, style: 'currency' },
                )}
              </span>
              /
              <span className="text-vestgrey-100">
                {formatNumber(
                  bigIntToDecimalStr(maxDrawdown, COLLATERAL_DECIMALS),
                  { digits: 2, style: 'currency' },
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
